import { installBackendAvailability } from './utils/backendAvailability';
import {isPublicPath} from './public/routes';
import PublicMetadata from './public/Metadata';
import AdminProfessionalStories from './pages/AdminProfessionalStories';
import AdminPsychologistPlans from './pages/AdminPsychologistPlans';
import ComplimentaryPlanPage from './pages/ComplimentaryPlanPage';
import PublicPsychologist from './pages/PublicPsychologist';
import { startBrowserPresence, stopBrowserPresence, documentHeaders, admitBrowserDocument } from "./utils/browserPresence";
import AccountSessionWarning from "./components/AccountSessionWarning";
import { openBrowserSessionChannel, sessionIdentity } from "./utils/browserSession";
import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ClientDashboard from "./pages/ClientDashboard";
import PsychologistDashboard from "./pages/PsychologistDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import AdminAIUsage from "./pages/AdminAIUsage";
import AdminLLMSettings from "./pages/AdminLLMSettings";
import SessionPhases from "./pages/SessionPhases";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import TrainingModule from "./pages/TrainingModule";
import useSessionTimeout from "./hooks/useSessionTimeout";
import SessionTimeoutWarning from "./components/SessionTimeoutWarning";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import GlobalLoadingBar from "./components/GlobalLoadingBar";
import NexusRoleplaySimulator from "./components/NexusRoleplaySimulator";
import { installInsightMutationInvalidation } from "./utils/insightInvalidation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
installInsightMutationInvalidation();

// Confirm connectivity independently; never replay application requests.
installBackendAvailability(axios, API, toast);

export const AuthContext = React.createContext();
const AUTH_STORAGE_KEYS = ["token", "user", "userRole", "role", "isAuthenticated"];
const LOGOUT_BROADCAST_KEY = "authLogout";

const clearClientAuth = () => {
  stopBrowserPresence();
  AUTH_STORAGE_KEYS.forEach((key) => {
    sessionStorage.removeItem(key);
    // One-time migration: old releases persisted credentials here. Do not
    // touch any unrelated settings, drafts, or workflow state.
    localStorage.removeItem(key);
  });
  delete axios.defaults.headers.common["Authorization"];
};

// Never let an already-mounted component send a captured bearer token after
// another tab (or this tab) has removed the active token.
axios.interceptors.request.use(async (config) => {
  const activeToken = sessionStorage.getItem("token");
  if (!activeToken) {
    if (config.headers) delete config.headers.Authorization;
  } else {
    if (!/\/auth\/(login|register(?:-with-photo)?)$/.test(config.url || "")) await admitBrowserDocument(API, activeToken);
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${activeToken}`;
  }
  config.headers = { ...config.headers, ...documentHeaders() };
  return config;
});

function PublicDocument() { React.useEffect(() => { window.location.reload(); }, []); return null; }

// Inner component that uses the session timeout hook inside Router context
function AppContent({ user, login, logout }) {
  // Session timeout hook - now inside Router context
  const { showWarning, timeLeft, extendSession, handleLogout } = useSessionTimeout(logout, user?.id);

  return (
    <>
      {user && <AccountSessionWarning key={user.id} api={API} userId={user.id} />}
      <PublicMetadata />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/problem" element={<PublicDocument />} />
        <Route path="/about" element={<PublicDocument />} />
        <Route path="/process" element={<PublicDocument />} />
        <Route path="/solution" element={<PublicDocument />} />
        <Route path="/practice" element={<PublicDocument />} />
        <Route path="/research" element={<PublicDocument />} />

        <Route path="/what-is-insight" element={<PublicDocument />} />
<Route path="/who-is-it-for" element={<PublicDocument />} />
<Route path="/why-insight" element={<PublicDocument />} />
<Route path="/five-phases" element={<PublicDocument />} />
<Route path="/key-outputs" element={<PublicDocument />} />
<Route path="/pricing" element={<PublicDocument />} />
        <Route path="/psychologist/my-plan" element={user?.role === "psychologist" ? <ComplimentaryPlanPage authenticated /> : <Navigate to="/login" replace />} />
        <Route path="/p/:slug" element={<PublicPsychologist />} />
        <Route path="/login" element={!user ? <Login /> : <Navigate to={`/${user.role}-dashboard`} />} />
        <Route path="/register" element={!user ? <Register /> : <Navigate to={`/${user.role}-dashboard`} />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/admin-dashboard" element={user?.role === "admin" ? <AdminDashboard /> : <Navigate to="/login" replace />} />
        <Route path="/admin/professional-stories" element={user?.role === "admin" ? <AdminProfessionalStories /> : <Navigate to="/login" replace />} />
        <Route path="/admin/psychologist-plans" element={user?.role === "admin" ? <AdminPsychologistPlans /> : <Navigate to="/login" replace />} />
        <Route path="/admin/ai-usage" element={user?.role === "admin" ? <AdminAIUsage /> : <Navigate to="/login" replace />} />
        <Route path="/admin/llm-settings" element={user?.role === "admin" ? <AdminLLMSettings /> : <Navigate to="/login" replace />} />
        <Route path="/client-dashboard" element={user?.role === "client" ? <ClientDashboard /> : <Navigate to="/login" replace />} />
        <Route path="/psychologist-dashboard" element={user?.role === "psychologist" ? <PsychologistDashboard /> : <Navigate to="/login" replace />} />
        <Route path="/session/:sessionId" element={user ? <SessionPhases /> : <Navigate to="/login" replace />} />
        <Route path="/training-module" element={user ? <TrainingModule /> : <Navigate to="/login" replace />} />
      </Routes>
      
      {/* Session Timeout Warning */}
      {user && (
        <SessionTimeoutWarning
          isOpen={showWarning}
          timeLeft={timeLeft}
          onExtend={extendSession}
          onLogout={handleLogout}
        />
      )}

      {/* Global toast notifications (sonner) — does not conflict with Radix Dialogs */}
      <Toaster position="top-right" richColors closeButton />
    </>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const channel = openBrowserSessionChannel();
    let cancelled = false;
    let retryTimer;
    let retryDelay = 2000;
    const bootstrap = async () => {
      let token = sessionStorage.getItem("token");
      // The server lease decides whether restored/reloaded credentials are live.
      // Preserving them here lets a navigation reconnect within departure grace.
      if (!token) token = await channel.request();
      if (cancelled) return;
      if (token) {
        sessionStorage.setItem("token", token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        try {
          await startBrowserPresence(API, token);
          const res = await axios.get(`${API}/auth/me`);
          if (!cancelled) {
            sessionStorage.setItem("user", JSON.stringify(res.data));
            setUser(res.data);
          }
        } catch (error) {
          // Network interruptions do not destroy the token or release ownership.
          if (error.response?.status === 409) {
            stopBrowserPresence();
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('user');
            delete axios.defaults.headers.common.Authorization;
            sessionStorage.setItem('insightSessionEnded', error.response.data.detail.message);
          } else if (error.response?.status === 401) {
            clearClientAuth();
          } else if (!cancelled) {
            retryTimer = setTimeout(bootstrap, retryDelay);
            retryDelay = Math.min(retryDelay * 2, 30000);
            return;
          }
        }
      }
      if (!cancelled) setLoading(false);
    };
    bootstrap();
    return () => { cancelled = true; clearTimeout(retryTimer); channel.close(); };
  }, []);

  useEffect(() => {
    const expired = (event) => {
      if (sessionStorage.getItem("token") !== event.detail.token) return;
      clearClientAuth();
      setUser(null);
      sessionStorage.setItem("insightSessionEnded", "Your browser session ended after a connection interruption or suspension. Please sign in again. Saved assessment data is retained.");
    };
    const blocked = (event) => {
      if (sessionStorage.getItem("token") !== event.detail.token) return;
      stopBrowserPresence();
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("user");
      delete axios.defaults.headers.common.Authorization;
      sessionStorage.setItem("insightSessionEnded", event.detail.message);
      setUser(null);
    };
    window.addEventListener("insight-tab-blocked", blocked);
    window.addEventListener("insight-session-expired", expired);
    const handleStorage = (event) => {
      if (event.key === LOGOUT_BROADCAST_KEY && event.newValue) {
        let signal;
        try { signal = JSON.parse(event.newValue); } catch (_) { return; }
        if (!signal.sid || signal.sid !== sessionIdentity(sessionStorage.getItem("token") || '')) return;
        clearClientAuth();
        setUser(null);
        window.location.replace('/login');
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => { window.removeEventListener("insight-tab-blocked", blocked); window.removeEventListener("storage", handleStorage); window.removeEventListener("insight-session-expired", expired); };
  }, []);

  const login = (token, userData) => {
    sessionStorage.setItem("token", token);
    sessionStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("insightAuthLastActivity", String(Date.now()));
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    setUser(userData);
    void startBrowserPresence(API, token);
  };

  const logout = async () => {
    const token = sessionStorage.getItem("token");
    clearClientAuth();
    setUser(null);
    localStorage.setItem(LOGOUT_BROADCAST_KEY, JSON.stringify({ sid: sessionIdentity(token || ''), at: Date.now() }));
    // Use a one-off request because the global interceptor correctly refuses
    // to send bearer credentials once local authentication has been cleared.
    if (token) {
      try {
        await fetch(`${API}/auth/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, ...documentHeaders() },
          cache: "no-store",
          keepalive: true,
        });
      } catch (_) {
        // Client state stays logged out even if the server is unreachable.
      }
    }
    // A delayed logout response must not navigate away from a newer sign-in.
    if (!sessionStorage.getItem('token')) window.location.replace('/login');
  };

  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
    sessionStorage.setItem('user', JSON.stringify(updatedUserData));
  };

  // Public HTML can hydrate immediately; private routes retain the original auth bootstrap.
  if (loading && !isPublicPath(window.location.pathname)) {
    return <GlobalLoadingBar initialLoading />;
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser }}>
      <BrowserRouter>
        <GlobalLoadingBar />
        <AppContent user={user} login={login} logout={logout} />
        <NexusRoleplaySimulator />
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
